import {Platform} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {RootState} from '@shared/store';
import {menuTabs} from '@features/layout/screens/MainLayoutScreen/constants/mainLayout.constant';
import {getConfig} from '@shared/config/AppConfig';
import {getNovaBaseUrl, resolveAgencyUrl} from '@shared/config/agencyUrl';
import {apiPost} from '@features/auth/api/apiClient';
import {novaAuthHeaders} from '@shared/api/novaAuthHeaders';
import {endPoints} from '@features/auth/constants/endPoints';
import {NovaVoiceModule} from '@shared/nativeModules/NovaVoiceModule';
import {ActionData, ListData, NovaNavigationAction, SuggestedQuestion} from '@shared/components/NovaAI/NovaAIProps';
import {SUGGESTED_QUESTIONS_MOCK, NOVA_CAPABILITIES} from '@shared/components/NovaAI/constants/novaAI.constant';

export type NovaAIResponse = {
  reply: string;
  /** Transcribed text returned by the voice endpoint */
  transcript?: string;
  /** Slim data for table rendering */
  listData?: ListData;
  /** Full action.data for future use */
  actionData?: ActionData;
  /**
   * The action's type discriminator (e.g. "repairs_listed", "tasks_updated").
   * "tasks_updated" means Nova committed a task save — the open repair's data
   * on screen is stale and should be refetched.
   */
  actionType?: string;
  /** Present when the API returns a screen-navigation intent */
  navAction?: NovaNavigationAction;
};

/**
 * A step Nova reports while it works, on the streamed endpoint.
 *
 * `stage` is vocabulary, not prose — the backend says "working" and names the
 * tool, and the wording a technician reads is chosen here so it can be
 * translated. See Deployment/AUTH.md's sibling, the SSE notes in faster-ai-ml.
 */
export type NovaStage = {
  stage: string;
  /** The tool Nova is about to run, on the `working` stage. */
  tool?: string;
  /**
   * Identifiers Nova resolved for that tool, so the message can name what it
   * is working on. Ids only — the backend deliberately refuses to put a
   * free-text argument here, because progress is shown before anything has
   * been checked.
   */
  repair_id?: string;
  work_order_id?: string;
  asset_id?: string;
  part_id?: string;
  document_id?: string;
};

/** Extract structured data from the raw Nova AI JSON response */
function parseNovaResponse(json: any): NovaAIResponse {
  const reply: string = json?.reply ?? 'No response';
  const transcript: string | undefined = json?.transcript ?? undefined;

  // ── Navigation action (action.ScreenName present) ─────────────────────────
  const rawAction = json?.action;
  if (rawAction?.ScreenName) {
    const navAction: NovaNavigationAction = rawAction;
    return {reply, transcript, navAction};
  }

  // ── Data listing action (action.data present) ─────────────────────────────
  const actionData: ActionData | undefined = rawAction?.data ?? undefined;
  const actionType: string | undefined = rawAction?.type ?? undefined;
  const columns = actionData?.columns;
  const tableRows = actionData?.table_rows;
  // Include listData whenever columns are present (even with empty table_rows)
  // so BotStructuredMessage can render "No result found" for empty results.
  const listData: ListData | undefined =
    Array.isArray(columns) && columns.length > 0
      ? {columns, table_rows: Array.isArray(tableRows) ? tableRows : []}
      : undefined;
  return {reply, transcript, listData, actionData, actionType};
}

/**
 * Nova's server-sent events, read from text in whatever pieces the network
 * delivers. A frame ends at a blank line and can be split across pieces —
 * treating each piece as a whole frame silently drops the answer — so a
 * partial tail waits for the next one. Shared by the browser's transport and
 * the phone's, so the two cannot read a stream differently.
 *
 * `push` throws when Nova reports an error; `finish` returns the answer, or
 * throws when the stream ended without one.
 */
const createSseReader = (onStage?: (stage: NovaStage) => void) => {
  let buffer = '';
  let finalJson: any = null;

  const handleFrame = (frame: string) => {
    let event = 'message';
    const data: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        data.push(line.slice(5).trim());
      }
      // A line starting with ':' is a comment (a heartbeat) — ignored.
    }
    if (!data.length) {
      return;
    }
    let parsed: any;
    try {
      parsed = JSON.parse(data.join('\n'));
    } catch {
      return;
    }
    if (event === 'stage') {
      onStage?.(parsed as NovaStage);
    } else if (event === 'final') {
      finalJson = parsed;
    } else if (event === 'error') {
      throw new Error(parsed?.message ?? 'Nova stream reported an error');
    }
  };

  return {
    push: (text: string) => {
      buffer += text;
      let split = buffer.indexOf('\n\n');
      while (split !== -1) {
        handleFrame(buffer.slice(0, split));
        buffer = buffer.slice(split + 2);
        split = buffer.indexOf('\n\n');
      }
    },
    finish: (): any => {
      if (buffer.trim()) {
        handleFrame(buffer);
        buffer = '';
      }
      if (!finalJson) {
        throw new Error('Nova stream ended without an answer');
      }
      return finalJson;
    },
  };
};

/**
 * How long the phone's stream may go quiet: twice the plain call's 30 s, as a
 * turn that reports its progress is getting somewhere. React Native applies it
 * to the silence between pieces on iOS, and to the whole call on Android.
 */
const STREAM_TIMEOUT_MS = 60000;

/**
 * The stream on a phone. React Native's fetch has no body to read while it
 * arrives, but its XMLHttpRequest does: with an onprogress handler set before
 * send(), the networking module hands the text over as it comes, on iOS and
 * Android alike, and responseText grows with it.
 */
const streamOverXhr = (
  url: string,
  headers: Record<string, string>,
  body: string,
  onStage?: (stage: NovaStage) => void,
): Promise<any> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const sse = createSseReader(onStage);
    let read = 0;
    let settled = false;
    const fail = (error: unknown) => {
      if (settled) {
        return;
      }
      settled = true;
      xhr.abort();
      reject(error);
    };
    const ok = () => xhr.status >= 200 && xhr.status < 300;
    const takeNewText = () => {
      const text = xhr.responseText ?? '';
      if (text.length > read) {
        const piece = text.slice(read);
        read = text.length;
        sse.push(piece);
      }
    };

    xhr.open('POST', url);
    Object.entries(headers).forEach(([name, value]) =>
      xhr.setRequestHeader(name, value),
    );
    xhr.timeout = STREAM_TIMEOUT_MS;
    xhr.onprogress = () => {
      // An error page is not a stream: its status settles it on load.
      if (settled || !ok()) {
        return;
      }
      try {
        takeNewText();
      } catch (error) {
        fail(error);
      }
    };
    xhr.onload = () => {
      if (settled) {
        return;
      }
      if (!ok()) {
        fail(new Error(`Nova stream unavailable (${xhr.status})`));
        return;
      }
      try {
        takeNewText();
        const answer = sse.finish();
        settled = true;
        resolve(answer);
      } catch (error) {
        fail(error);
      }
    };
    xhr.onerror = () => fail(new Error('Nova stream failed'));
    xhr.ontimeout = () => fail(new Error('Nova stream went quiet'));
    xhr.send(body);
  });

export const useNovaAIChatWindowController = () => {
  const {t} = useTranslation();
  const technicianId = useSelector(
    (state: RootState) => state.technician.selectedTechnician.selectedTechnicianId,
  );
  const shopId = useSelector(
    (state: RootState) => state.shop.selectedShop.id,
  );
  const activeTab = useSelector((state: RootState) => state.menu.activeTab);
  const activeSubTab = useSelector((state: RootState) => state.menu.activeSubTab);
  const activeFlowScreen = useSelector((state: RootState) => state.menu.activeFlowScreen);
  // The repair open in RepairDetailsScreen ('' when none). Sent as
  // screen_context so the backend can resolve "this repair" — task, note and
  // parts questions are asked against whatever repair is on screen.
  const selectedRepairId = useSelector(
    (state: RootState) => state.repair.selectedRepairId,
  );
  // The open repair's work order (its documentId), set by RepairDetailsScreen.
  // Sent alongside the repair so "change this work order's status" resolves to
  // the number in the header without Nova having to re-read the repair.
  const selectedRepairWorkOrderId = useSelector(
    (state: RootState) => state.repair.selectedRepairWorkOrderId,
  );
  // What the open repair's card calls it, so a reply can name the repair the
  // technician is looking at rather than an id no screen shows.
  const selectedRepairLabel = useSelector(
    (state: RootState) => state.repair.selectedRepairLabel,
  );
  const configuredSelection = useSelector(
    (state: RootState) => state.repair.configuredSelection,
  );
  const selectedNoteId = useSelector(
    (state: RootState) => state.repair.selectedNoteId,
  );
  // The unsaved edit on the Tasks tab (resolution tapped, comment typed),
  // published by TasksView. Sent so "save tasks" commits what is on screen.
  const taskEdit = useSelector((state: RootState) => state.repair.taskEdit);
  // The open repair's tab showing and, on Notes or Attachments, which of their
  // own: "switch to notes" on the Notes tab is answered "You're already on it".
  const repairTabShown = useSelector(
    (state: RootState) => state.repair.repairTabShown,
  );
  const repairTabSectionShown = useSelector(
    (state: RootState) => state.repair.repairTabSectionShown,
  );
  // The Repairs list on screen — its Shop Scope and its search box — so a
  // status switch answers about that list and not a default one.
  const repairListView = useSelector(
    (state: RootState) => state.repair.repairListView,
  );
  // The repair cards on screen, in order, so "open the second repair" is the
  // second card — Nova otherwise counted a list it had shown in the chat.
  const repairListRows = useSelector(
    (state: RootState) => state.repair.repairListRows,
  );
  // The parts search on the repair's Parts tab, so "request the first part from
  // the search result" means the rows on screen rather than a search Nova ran
  // itself earlier in the session.
  const partsSearchView = useSelector(
    (state: RootState) => state.repair.partsSearchView,
  );

  /**
   * Derives the current screen and tab context to send with every Nova AI
   * request so the backend can tailor responses to what the user is viewing.
   *
   * currentScreen:
   *   - "New Work Order" | "New Repair"  — when a create-flow overlay is open
   *   - "Home" | "Repairs" | "Parts" | "VIN Scanner" | "Profile" | "Help"
   *     — the active main menu tab otherwise
   *
   * currentTab:
   *   - "Repairs" or "Parts Inventory"  — when on the Home screen
   *   - null for every other screen (no visible sub-tabs)
   */
  // Sent whether or not a repair is open: the Repairs list is a screen of its
  // own, and `screen_context` is otherwise only built for an open repair.
  const repairListContext = repairListView
    ? {
        repair_list: {
          scope: repairListView.scope,
          ...(repairListView.status ? {status: repairListView.status} : {}),
          ...(repairListView.priority ? {priority: repairListView.priority} : {}),
          ...(repairListView.searchField
            ? {search_field: repairListView.searchField}
            : {}),
          ...(repairListView.searchValue
            ? {search_value: repairListView.searchValue}
            : {}),
        },
      }
    : {};

  const screenContext: Record<string, unknown> = {
    ...(selectedRepairId
      ? {
          repair_id: String(selectedRepairId),
          ...(selectedRepairWorkOrderId
            ? {work_order_id: String(selectedRepairWorkOrderId)}
            : {}),
          ...(selectedRepairLabel ? {repair_label: selectedRepairLabel} : {}),
          ...(partsSearchView ? {parts_search: partsSearchView} : {}),
          // What is ticked on the Configured Part List: "submit request" reads
          // the parts and quantities back before it posts them. Absent when the
          // panel is not mounted, which Nova reads as "not told" rather than as
          // nothing ticked.
          ...(configuredSelection ? {configured_selection: configuredSelection} : {}),
          // The note open in the Notes modal, so "this note" is that one.
          ...(selectedNoteId ? {note_id: String(selectedNoteId)} : {}),
          // The repair's tab showing, and its own tab on Notes or Attachments.
          ...(repairTabShown ? {repair_tab: repairTabShown} : {}),
          ...(repairTabShown && repairTabSectionShown
            ? {repair_tab_section: repairTabSectionShown}
            : {}),
          // The task card being edited, with its unsaved values.
          ...(taskEdit
            ? {
                task_edit: {
                  step_number: taskEdit.stepNumber,
                  ...(taskEdit.resultId != null ? {result_id: taskEdit.resultId} : {}),
                  ...(taskEdit.resultName ? {result_name: taskEdit.resultName} : {}),
                  comment: taskEdit.comment,
                },
              }
            : {}),
        }
      : {}),
    ...repairListContext,
    ...(repairListRows?.length ? {repair_rows: repairListRows} : {}),
  };

  const currentScreen: string = activeFlowScreen ?? activeTab;
  const currentTab: string | null =
    activeTab === menuTabs.home && !activeFlowScreen ? activeSubTab : null;

  const streamHeaders = async (): Promise<Record<string, string>> => ({
    ...(await novaAuthHeaders()),
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  });

  const chatPayload = (userText: string, sessionId: string) => ({
    text: userText,
    session_id: sessionId,
    technician_id: technicianId ? Number(technicianId) : 0,
    shop_id: shopId ? Number(shopId) : 0,
    currentScreen: currentScreen,
    currentTab: currentTab,
    // Omitted (not sent as empty) when there is nothing on screen worth
    // describing, matching the backend's "absent means it does not
    // apply" reading. A repair being open is no longer the only case:
    // the Repairs list is a screen of its own.
    ...(Object.keys(screenContext).length ? {screen_context: screenContext} : {}),
  });

  /**
   * One turn over `/api/chat/stream`, reporting progress as it arrives.
   *
   * Throws on anything unexpected so the caller can fall back to the plain
   * endpoint — the two exist side by side on the backend precisely so that a
   * failure here costs a slower turn rather than a lost one.
   *
   * EventSource is unusable for this: it is GET-only and cannot carry a JSON
   * body or these headers. So the frames are read by hand (createSseReader):
   * off the body stream in a browser, off XMLHttpRequest's growing text on a
   * phone, whose fetch has no body stream to read.
   */
  const streamTextFromBackend = async (
    novaBaseUrl: string,
    userText: string,
    sessionId: string,
    onStage?: (stage: NovaStage) => void,
  ): Promise<NovaAIResponse> => {
    const url = `${novaBaseUrl}${endPoints.novaAI.chatStream}`;
    const body = JSON.stringify(chatPayload(userText, sessionId));

    if (Platform.OS !== 'web') {
      // The phone's session is the bearer token in the headers; no cookie.
      const answer = await streamOverXhr(url, await streamHeaders(), body, onStage);
      return parseNovaResponse(answer);
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: await streamHeaders(),
      // The web session is cookie-only; without this the cookie never leaves.
      credentials: 'include',
      body,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Nova stream unavailable (${response.status})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const sse = createSseReader(onStage);

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const {done, value} = await reader.read();
      if (done) {
        break;
      }
      sse.push(decoder.decode(value, {stream: true}));
    }
    // Parsed by the same function as the plain endpoint, so the two transports
    // cannot disagree about what a reply means.
    return parseNovaResponse(sse.finish());
  };

  const sendTextToBackend = async (
    userText: string,
    sessionId: string,
    onStage?: (stage: NovaStage) => void,
  ): Promise<NovaAIResponse> => {
    // Read once. Calling getConfig() a second time for the flag consumed a
    // `mockReturnValueOnce` in the controller's tests and left the real base
    // URL unread — a mocking artefact, but it would be a real one anywhere the
    // config is resolved lazily or changes between calls.
    const config = getConfig();
    const novaBaseUrl = resolveAgencyUrl(config?.NOVA_AI_BASE_URL);

    // Streaming is opt-in (NOVA_AI_STREAMING), on web and on the phone alike.
    if (config?.NOVA_AI_STREAMING) {
      try {
        return await streamTextFromBackend(novaBaseUrl, userText, sessionId, onStage);
      } catch (e) {
        // A slower turn, not a lost one.
        console.warn('Nova AI stream failed, falling back to /api/chat:', e);
      }
    }
    try {
      const response = await apiPost({
        endpoint: `${novaBaseUrl}${endPoints.novaAI.chat}`,
        payload: chatPayload(userText, sessionId),
      });
      const json = response.data as any;
      return parseNovaResponse(json);
    } catch (e) {
      console.warn('Nova AI text chat error:', e);
      return {reply: 'Sorry, I was unable to process your request. Please try again.'};
    }
  };

  /**
   * Voice turn: transcribe the recording, then send the text through the normal
   * chat path.
   *
   * Two calls rather than one because Nova has no combined voice endpoint. The
   * old `/api/voice` (STT + agent + TTS in one request) was dropped when Nova
   * moved to its scenario registry, and `/api/stt/transcribe` replaced the STT
   * half; this controller was still posting to the removed route, so the chat
   * mic had been answering 404 while the note modal — which already used
   * `/api/stt/transcribe` — kept working.
   *
   * Splitting it also means a voice turn and a typed turn reach the agent by
   * exactly the same route, so the two cannot drift.
   */
  const sendAudioToBackend = async (audioFilePath: string, sessionId: string): Promise<NovaAIResponse> => {
    try {
      const novaBaseUrl = getNovaBaseUrl();
      const formData = new FormData();

      if (Platform.OS === 'web') {
        // Take the Blob from the recorder rather than fetching the blob: URL
        // it also hands out: `fetch(blob:...)` counts as connect-src, which
        // this app's CSP blocks, so the upload fails before any request is
        // made (and never appears in the Network tab). The note modal's
        // recorder avoids the same trap by passing its Blob directly.
        const blob = NovaVoiceModule.getLastRecordingBlob?.();
        if (!blob || blob.size === 0) {
          return {reply: t('novaAI.voiceNotRecognised')};
        }
        // The extension has to match the actual container: the STT provider
        // picks its decoder from the filename, and a WebM recording named
        // .wav (or the reverse) fails upstream with a 500.
        const type = blob.type || 'audio/webm';
        const ext = type.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `recording.${ext}`, {type});
        formData.append('file', file);
      } else {
        // Mobile – strip extra file:// if present then re-add for Android
        const cleanPath = audioFilePath.replace(/^file:\/\//, '');
        const fileUri =
          Platform.OS === 'android' ? `file://${cleanPath}` : audioFilePath;
        let fileName = cleanPath.split('/').pop() || 'audio.wav';
        // React Native on Android needs audio/mp4 for .m4a files
        if (fileName.endsWith('.m4a')) {
          fileName = fileName.replace(/\.m4a$/, '.mp4');
        }
        formData.append('file', {
          uri: fileUri,
          type: 'audio/mp4',
          name: fileName,
        } as any);
      }

      // Plain fetch, no auth: the STT endpoint is public, matching the note
      // modal's recorder (useVoiceRecorder.web.ts).
      // Guarded since 24 Sep 2026: this endpoint takes an upload and spends
      // AiDE credit on it, so it authenticates like the rest. FormData sets
      // its own Content-Type, so the shared helper deliberately omits one.
      const sttResponse = await fetch(
        `${novaBaseUrl}${endPoints.novaAI.transcribe}`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: {
            ...(await novaAuthHeaders()),
            Accept: 'application/json, text/plain, */*',
          },
        },
      );
      if (!sttResponse.ok) {
        throw new Error(`Transcription failed: ${sttResponse.status}`);
      }

      // Shapes vary by deployment: the BFF may wrap the payload in `data`.
      const raw = await sttResponse.json();
      const payload = raw?.data ?? raw;
      const transcript: string | undefined =
        payload?.transcription ??
        payload?.transcript ??
        payload?.text ??
        (typeof payload === 'string' ? payload : undefined);

      if (!transcript || !transcript.trim()) {
        // Nothing was said, or nothing could be made out. Said plainly rather
        // than as an error: it is a normal outcome of a noisy shop floor.
        return {reply: t('novaAI.voiceNotRecognised')};
      }

      // The transcript now goes through exactly the path a typed message takes.
      const response = await sendTextToBackend(transcript, sessionId);
      return {...response, transcript};
    } catch (e) {
      console.warn('Nova AI voice error:', e);
      return {reply: 'Sorry, I was unable to process your request. Please try again.'};
    }
  };

  /**
   * Mock API — fetches suggested questions for the expanded right panel.
   * Replace the implementation body with a real `apiGet` call when the
   * backend endpoint is ready; the return type and call-site are unchanged.
   */
  const fetchSuggestedQuestions = async (): Promise<{
    questions: SuggestedQuestion[];
    capabilities: string[];
  }> => {
    // Simulate network latency
    await new Promise<void>(resolve => setTimeout(resolve, 400));
    return {
      questions: SUGGESTED_QUESTIONS_MOCK,
      capabilities: NOVA_CAPABILITIES,
    };
  };

  return {sendTextToBackend, sendAudioToBackend, fetchSuggestedQuestions};
};
